/**
 * LeadDetailModal — read view + development logs + quick actions (Leads v2).
 */
import { useState } from "react";
import {
  Phone,
  Envelope,
  WhatsappLogo,
  CalendarCheck,
  ChatText,
  UserSwitch,
  Pencil,
  Plus,
  Clock,
  UserCircle,
  Plugs,
  CheckCircle,
} from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import RichTextEditor from "../ui/RichTextEditor";
import LoaderLoop from "../shared/loader/LoaderLoop";
import DatePicker from "../ui/DatePicker";
import TimePicker from "../ui/TimePicker";
import LeadStatusSelect from "./LeadStatusSelect";
import { getLeadLostReasonLabel, getLeadSourceChannel } from "../../utils/leadContract";
import {
  statusStyleVars,
  displayPhone,
  whatsappHref,
  telHref,
  getFollowUpState,
  FOLLOW_UP_LABELS,
  formatFollowUp,
  formatLeadDate,
  sourceChannelLabel,
  combineDateTime,
} from "./leadsUtils";

const formatLogDate = (iso) => formatLeadDate(iso, true);

function LeadDetailModal({
  lead,
  status,
  statuses,
  isAddingLog = false,
  isBusy = false,
  onClose,
  onStatusChange, // (lead, value)
  onConvert,
  onEdit,
  onAddLog, // (content) => Promise
  onMarkContacted, // (nextFollowUpAt: Date|null|undefined) => Promise
}) {
  const [newLog, setNewLog] = useState("");
  const [contactPanel, setContactPanel] = useState(false);
  const [nextDate, setNextDate] = useState("");
  const [nextTime, setNextTime] = useState("");

  if (!lead) return null;
  const follow = getFollowUpState(lead);
  const channel = getLeadSourceChannel(lead);
  const wa = whatsappHref(lead.phone);
  const tel = telHref(lead.phone);
  const isArchived = lead.status === "converted" || lead.status === "lost" || !!lead.convertedToPatientId;

  const submitLog = async () => {
    if (!newLog.trim()) return;
    await onAddLog?.(newLog);
    setNewLog("");
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal ld-modal ld-detail" onClick={(e) => e.stopPropagation()} style={statusStyleVars(status)}>
        <div className="modal-header ld-detail-header">
          <div className="ld-detail-heading">
            <span className="ld-detail-icon">
              <UserCircle size={20} weight="fill" />
            </span>
            <div className="ld-modal-heading">
              <span className="ld-eyebrow">
                {sourceChannelLabel(lead)}
                {channel === "webhook" && lead.source?.keyLabel ? ` · ${lead.source.keyLabel}` : ""}
                {lead.createdAt ? ` · ${formatLeadDate(lead.createdAt)}` : ""}
              </span>
              <h3>{lead.name}</h3>
            </div>
          </div>
          <div className="ld-detail-head-actions">
            <span className="ld-badge">{status?.label || "לא ידוע"}</span>
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>

        <div className="modal-body ld-detail-body">
          {/* Status + assignment controls */}
          <div className="ld-detail-controls">
            <label className="ld-control">
              <span className="ld-control-label">סטטוס</span>
              <LeadStatusSelect
                value={lead.status}
                status={status}
                statuses={statuses}
                excludeValues={["converted"]}
                disabled={isArchived}
                busy={isBusy}
                size="md"
                onChange={(v) => onStatusChange?.(lead, v)}
              />
            </label>
            <div className="ld-control">
              <span className="ld-control-label">מעקב הבא</span>
              <span className={`ld-control-value${follow ? ` ld-follow-${follow}` : ""}`}>
                <Clock size={14} weight="regular" />
                {lead.nextFollowUpAt ? `${formatFollowUp(lead)}${follow && follow !== "upcoming" ? ` · ${FOLLOW_UP_LABELS[follow]}` : ""}` : "לא נקבע"}
              </span>
            </div>
          </div>

          {lead.status === "lost" && (lead.lostReason || lead.lostNote) && (
            <div className="ld-lost-banner">
              <span>סיבת סגירה: {getLeadLostReasonLabel(lead.lostReason) || "אחר"}</span>
              {lead.lostNote && <span className="ld-lost-note">{lead.lostNote}</span>}
            </div>
          )}

          {/* Info grid */}
          <section className="ld-section">
            <h4>פרטי מתעניין</h4>
            <div className="ld-info-grid">
              {lead.phone && (
                <div className="ld-info-item">
                  <Phone size={15} weight="regular" />
                  <a href={tel || undefined} dir="ltr">{displayPhone(lead.phone)}</a>
                  {wa && (
                    <a className="ld-icon-btn ld-icon-wa ld-inline" href={wa} target="_blank" rel="noopener noreferrer" title="שלח WhatsApp" aria-label="שלח WhatsApp">
                      <WhatsappLogo size={15} weight="regular" />
                    </a>
                  )}
                </div>
              )}
              {lead.email && (
                <div className="ld-info-item">
                  <Envelope size={15} weight="regular" />
                  <a href={`mailto:${lead.email}`} dir="ltr">{lead.email}</a>
                </div>
              )}
              {lead.referralSource && (
                <div className="ld-info-item">
                  <span className="ld-info-label">מקור הפניה</span>
                  <span>{lead.referralSource}</span>
                </div>
              )}
              {lead.source?.campaign && lead.source.campaign !== lead.referralSource && (
                <div className="ld-info-item">
                  <span className="ld-info-label">קמפיין</span>
                  <span>{lead.source.campaign}</span>
                </div>
              )}
              {channel === "webhook" && (
                <div className="ld-info-item">
                  <Plugs size={15} weight="regular" />
                  <span>נקלט אוטומטית ב-{formatLeadDate(lead.createdAt, true)}</span>
                </div>
              )}
              {lead.preferredDaysAndTimes && (
                <div className="ld-info-item">
                  <CalendarCheck size={15} weight="regular" />
                  <span>{lead.preferredDaysAndTimes}</span>
                </div>
              )}
              {lead.proposedSessionCost ? (
                <div className="ld-info-item">
                  <span className="ld-info-label">עלות שהוצעה</span>
                  <span>₪{lead.proposedSessionCost}</span>
                </div>
              ) : null}
              {lead.agreedSessionCost ? (
                <div className="ld-info-item">
                  <span className="ld-info-label">עלות שסוכמה</span>
                  <span>₪{lead.agreedSessionCost}</span>
                </div>
              ) : null}
              {lead.lastContactAt && (
                <div className="ld-info-item">
                  <CheckCircle size={15} weight="regular" />
                  <span>קשר אחרון: {formatLeadDate(lead.lastContactAt, true)}</span>
                </div>
              )}
            </div>
            {lead.notes && (
              <div className="ld-notes">
                <span className="ld-info-label">הערות</span>
                <p>{lead.notes}</p>
              </div>
            )}
          </section>

          {/* Quick action: mark contacted */}
          {!isArchived && (
            <section className="ld-section ld-contact-section">
              {!contactPanel ? (
                <button type="button" className="ld-secondary-btn" onClick={() => setContactPanel(true)} disabled={isBusy}>
                  <CheckCircle size={16} weight="bold" />
                  <span>סמן שנוצר קשר</span>
                </button>
              ) : (
                <div className="ld-contact-panel">
                  <span className="ld-info-label">נוצר קשר עכשיו. לקבוע מעקב הבא?</span>
                  <div className="form-row">
                    <div className="form-group">
                      <label>תאריך</label>
                      <DatePicker value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>שעה</label>
                      <TimePicker value={nextTime} onChange={(e) => setNextTime(e.target.value)} disabled={!nextDate} />
                    </div>
                  </div>
                  <div className="ld-contact-actions">
                    <button type="button" className="btn-secondary" onClick={() => setContactPanel(false)} disabled={isBusy}>
                      ביטול
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={isBusy}
                      onClick={async () => {
                        await onMarkContacted?.(undefined);
                        setContactPanel(false);
                      }}
                    >
                      ללא מעקב
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={isBusy || !nextDate}
                      onClick={async () => {
                        await onMarkContacted?.(combineDateTime(nextDate, nextTime));
                        setContactPanel(false);
                        setNextDate("");
                        setNextTime("");
                      }}
                    >
                      {isBusy && <LoaderLoop size="xs" />}
                      <span>שמור מעקב</span>
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Development logs */}
          <section className="ld-section">
            <h4>
              <ChatText size={17} weight="fill" />
              תיעודי התפתחות
            </h4>
            <div className="ld-add-log">
              <RichTextEditor value={newLog} onChange={setNewLog} placeholder="הוסף תיעוד התפתחות חדש..." />
              <button className="btn-primary ld-add-log-btn" onClick={submitLog} disabled={!newLog.trim() || isAddingLog}>
                {isAddingLog ? <LoaderLoop size="xs" /> : <Plus size={16} weight="bold" />}
                <span>הוסף תיעוד</span>
              </button>
            </div>
            <div className="ld-logs">
              {lead.developmentLogs?.length > 0 ? (
                [...lead.developmentLogs].reverse().map((log) => (
                  <div key={log.id} className="ld-log">
                    <div className="ld-log-head">
                      <span className="ld-log-author">{log.authorName}</span>
                      <span className="ld-log-date">{formatLogDate(log.createdAt)}</span>
                    </div>
                    <div className="ld-log-content rich-text-display" dangerouslySetInnerHTML={{ __html: log.content }} />
                  </div>
                ))
              ) : (
                <div className="ld-logs-empty">
                  <ChatText size={28} weight="light" />
                  <p>תעד/י שיחות, מיילים והתקדמות — הכל במקום אחד.</p>
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="modal-footer ld-detail-footer">
          <button className="btn-secondary" onClick={onClose}>
            סגור
          </button>
          <button className="btn-secondary" onClick={() => onEdit?.(lead)} disabled={isBusy}>
            <Pencil size={16} weight="regular" />
            <span>ערוך</span>
          </button>
          {!isArchived && (
            <button className="btn-primary" onClick={() => onConvert?.(lead)} disabled={isBusy}>
              <UserSwitch size={16} weight="regular" />
              <span>הפוך למטופל</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default LeadDetailModal;
